import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import {
  Building2,
  CalendarDays,
  CreditCard,
  Home,
  Inbox,
  Handshake,
  ListTodo,
  Mail,
  Menu,
  Plus,
  Settings,
  Users,
} from "lucide-react";
import { useTranslate } from "ra-core";
import { Link, matchPath, useLocation, useMatch } from "react-router";
import { ContactCreateSheet } from "../contacts/ContactCreateSheet";
import { useState } from "react";
import { NoteCreateSheet } from "../notes/NoteCreateSheet";
import { TaskCreateSheet } from "../tasks/TaskCreateSheet";
import { getDefaultEventStart } from "../events/calendarUtils";
import { EventCreateDialog } from "../events/EventDialogs";

export const MobileNavigation = () => {
  const location = useLocation();
  const translate = useTranslate();

  let currentPath: string | boolean = "/";
  if (matchPath("/", location.pathname)) {
    currentPath = "/";
  } else if (matchPath("/contacts/*", location.pathname)) {
    currentPath = "/contacts";
  } else if (matchPath("/companies/*", location.pathname)) {
    currentPath = "/companies";
  } else if (matchPath("/tasks/*", location.pathname)) {
    currentPath = "/tasks";
  } else if (matchPath("/deals/*", location.pathname)) {
    currentPath = "/deals";
  } else if (matchPath("/events/*", location.pathname)) {
    currentPath = "/events";
  } else if (matchPath("/settings/*", location.pathname)) {
    currentPath = "/settings";
  } else if (matchPath("/partners/*", location.pathname)) {
    currentPath = "/partners";
  } else {
    currentPath = false;
  }

  // Check if the app is running as a PWA (standalone mode)
  const isPwa = window.matchMedia("(display-mode: standalone)").matches;
  // Check if it's iOS on the web
  const isWebiOS = /iPad|iPod|iPhone/.test(window.navigator.userAgent);

  return (
    <nav
      aria-label={translate("crm.navigation.label")}
      className="fixed bottom-0 left-0 right-0 z-50 bg-secondary h-14"
      style={{
        // iOS bug: even though viewport is set correctly, the bottom safe area inset is not accounted for
        // So we manually add some padding to avoid the navigation being too close to the home bar
        paddingBottom: isPwa && isWebiOS ? 15 : undefined,
        // We use box-sizing: border-box, so the height contains the padding.
        // To actually increase the padding, we need to increase the height as well
        height:
          "calc(var(--spacing)) * 6" + (isPwa && isWebiOS ? " + 15px" : ""),
      }}
    >
      <div className="flex justify-center">
        <>
          <NavigationButton
            href="/"
            Icon={Home}
            label={translate("crm.navigation.home")}
            isActive={currentPath === "/"}
          />
          <NavigationButton
            href="/companies"
            Icon={Building2}
            label={translate("resources.companies.name", {
              smart_count: 2,
            })}
            isActive={currentPath === "/companies"}
          />
          <NavigationButton
            href="/contacts"
            Icon={Users}
            label={translate("resources.contacts.name", {
              smart_count: 2,
            })}
            isActive={currentPath === "/contacts"}
          />
          <CreateButton />
          <NavigationButton
            href="/events"
            Icon={CalendarDays}
            label={translate("resources.events.name", { smart_count: 2 })}
            isActive={currentPath === "/events"}
          />
          <MoreMenu
            isActive={
              currentPath === "/tasks" ||
              currentPath === "/partners" ||
              currentPath === "/settings"
            }
          />
        </>
      </div>
    </nav>
  );
};

const NavigationButton = ({
  href,
  Icon,
  label,
  isActive,
}: {
  href: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  label: string;
  isActive: boolean;
}) => (
  <Button
    asChild
    variant="ghost"
    className={cn(
      "flex-col gap-1 h-auto py-2 px-1 rounded-md w-14",
      isActive ? null : "text-muted-foreground",
    )}
  >
    <Link to={href}>
      <Icon className="size-6" />
      <span className="text-[0.6rem] font-medium">{label}</span>
    </Link>
  </Button>
);

const CreateButton = () => {
  const translate = useTranslate();
  const contact_id = useMatch("/contacts/:id/*")?.params.id;
  const company_id = useMatch("/companies/:id/*")?.params.id;
  const [contactCreateOpen, setContactCreateOpen] = useState(false);
  const [eventCreateOpen, setEventCreateOpen] = useState(false);
  const [noteCreateOpen, setNoteCreateOpen] = useState(false);
  const [taskCreateOpen, setTaskCreateOpen] = useState(false);

  return (
    <>
      <ContactCreateSheet
        open={contactCreateOpen}
        onOpenChange={setContactCreateOpen}
      />
      <NoteCreateSheet
        open={noteCreateOpen}
        onOpenChange={setNoteCreateOpen}
        contact_id={contact_id}
      />
      <TaskCreateSheet
        open={taskCreateOpen}
        onOpenChange={setTaskCreateOpen}
        contact_id={contact_id}
      />
      <EventCreateDialog
        open={eventCreateOpen}
        onClose={() => setEventCreateOpen(false)}
        record={{
          start_at: getDefaultEventStart(new Date()),
          contact_id: contact_id ? Number(contact_id) : undefined,
          company_id: company_id ? Number(company_id) : undefined,
        }}
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="default"
            size="icon"
            className="h-16 w-16 rounded-full -mt-3"
            aria-label={translate("ra.action.create")}
          >
            <Plus className="size-10" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem
            className="h-12 px-4 text-base"
            onSelect={() => {
              setContactCreateOpen(true);
            }}
          >
            {translate("resources.contacts.forcedCaseName")}
          </DropdownMenuItem>
          <DropdownMenuItem
            className="h-12 px-4 text-base"
            onSelect={() => {
              setNoteCreateOpen(true);
            }}
          >
            {translate("resources.notes.forcedCaseName")}
          </DropdownMenuItem>
          <DropdownMenuItem
            className="h-12 px-4 text-base"
            onSelect={() => {
              setTaskCreateOpen(true);
            }}
          >
            {translate("resources.tasks.forcedCaseName")}
          </DropdownMenuItem>
          <DropdownMenuItem
            className="h-12 px-4 text-base"
            onSelect={() => {
              setEventCreateOpen(true);
            }}
          >
            {translate("resources.events.name", { smart_count: 1 })}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};

/** Pages without a slot of their own in the bar. */
const MoreMenu = ({ isActive }: { isActive: boolean }) => {
  const translate = useTranslate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            "flex-col gap-1 h-auto py-2 px-1 rounded-md w-14",
            isActive ? null : "text-muted-foreground",
          )}
        >
          <Menu className="size-6" />
          <span className="text-[0.6rem] font-medium">
            {translate("crm.navigation.more")}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem asChild className="h-12 px-4 text-base">
          <Link to="/tasks">
            <ListTodo className="size-5" />
            {translate("resources.tasks.name", { smart_count: 2 })}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild className="h-12 px-4 text-base">
          <Link to="/partners">
            <Handshake className="size-5" />
            {translate("resources.partners.name", { smart_count: 2 })}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild className="h-12 px-4 text-base">
          <Link to="/mailbox">
            <Inbox className="size-5" />
            {translate("crm.mailbox.title")}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild className="h-12 px-4 text-base">
          <Link to="/mail-settings">
            <Mail className="size-5" />
            {translate("crm.mail.title")}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild className="h-12 px-4 text-base">
          <Link to="/settings">
            <Settings className="size-5" />
            {translate("crm.settings.title")}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild className="h-12 px-4 text-base">
          <Link to="/subscription">
            <CreditCard className="size-5" />
            {translate("crm.billing.title")}
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
